import { useCart } from '../contexts/CartContext';

interface CartModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartModal({ isOpen, onClose }: CartModalProps) {
  const { cart, removeFromCart, clearCart, getTotal } = useCart();

  if (!isOpen) return null;

  const handleWhatsAppCheckout = () => {
    const whatsappNumber = '5212291578562';
    
    let message = '¡Hola! Me gustaría reservar las siguientes embarcaciones:\n\n';
    
    cart.forEach((item, index) => {
      message += `${index + 1}. *${item.name}*\n`;
      message += `   📅 Fecha: ${item.date}\n`;
      message += `   ⏰ Duración: ${item.hours} horas\n`;
      message += `   👥 Pasajeros: ${item.passengers}\n`;
      message += `   💰 Precio: $${item.price.toLocaleString('es-MX')}\n\n`;
    });
    
    message += `*Total: $${getTotal().toLocaleString('es-MX')} MXN*\n\n`;
    message += '¿Podrían confirmar la disponibilidad?';
    
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden m-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <i className="ri-shopping-cart-line text-3xl"></i>
              <div>
                <h2 className="text-2xl font-bold">Carrito de Reservaciones</h2>
                <p className="text-teal-100 text-sm">{cart.length} {cart.length === 1 ? 'embarcación' : 'embarcaciones'}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-all cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-280px)] p-6">
          {cart.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-shopping-cart-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">Tu carrito está vacío</p>
              <p className="text-gray-400 text-sm mt-2">Agrega embarcaciones desde nuestra flota</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item) => (
                <div key={item.id} className="bg-gray-50 rounded-xl p-4 flex gap-4">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">{item.name}</h3>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p className="flex items-center gap-2">
                        <i className="ri-calendar-line"></i>
                        {item.date}
                      </p>
                      <p className="flex items-center gap-2">
                        <i className="ri-time-line"></i>
                        {item.hours} horas
                      </p>
                      <p className="flex items-center gap-2">
                        <i className="ri-group-line"></i>
                        {item.passengers} pasajeros
                      </p>
                    </div>
                    <p className="text-teal-600 font-bold mt-2">
                      ${item.price.toLocaleString('es-MX')} MXN
                    </p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="w-10 h-10 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center justify-center transition-all cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-delete-bin-line text-xl"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <span className="text-lg font-semibold text-gray-700">Total:</span>
              <span className="text-2xl font-bold text-teal-600">
                ${getTotal().toLocaleString('es-MX')} MXN
              </span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={clearCart}
                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Vaciar Carrito
              </button>
              <button
                onClick={handleWhatsAppCheckout}
                className="flex-1 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-lg"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Pagar por WhatsApp
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
